import apiInstance from '@/api';
import { ThunkApiConfig } from '@/models/ThunkApiConfig';
import { RootState } from '@/store/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { DealProgressSummary } from '../types';

export interface DealProgressState {
  // completed_steps keyed by gold_vision_id; absent until first loaded
  stepsByDeal: Record<number, number>;
  loading: boolean;
}

const initialState: DealProgressState = {
  stepsByDeal: {},
  loading: false,
};

// One batch call for any number of deals (also used for a single-deal refresh).
export const fetchDealProgressSummaries =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'dealProgress/fetchSummaries',
    async (goldVisionIds: number[]) => {
      const response = await apiInstance.deals.getDealProgressSummaries({
        gold_vision_ids: goldVisionIds,
      });
      return response.data.data as DealProgressSummary[];
    },
  );

const dealProgressSlice = createSlice({
  name: 'dealProgress',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDealProgressSummaries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDealProgressSummaries.fulfilled, (state, action) => {
        state.loading = false;
        action.payload.forEach(({ gold_vision_id, completed_steps }) => {
          state.stepsByDeal[gold_vision_id] = completed_steps;
        });
      })
      // a failed refresh keeps whatever figures are already on screen
      .addCase(fetchDealProgressSummaries.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const selectDealProgressSteps = (state: RootState) =>
  state.dealProgress.stepsByDeal;

export default dealProgressSlice.reducer;
